import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useCallback, useState } from 'react'

import { Spinner } from '@authy/lib-client/components'

import { Link, LinkNavigationProps } from '@/components/Link'

import Illustration from './EmptyStateLockIllustration.svg'

type EmptyStateProps = {
    small?: boolean
    layout?: 'default' | 'overlay' | 'container'
    illustrationSrc: string
    title: string
    subtitle: string
    actionCaption?: string
    actionLinkProps?: LinkNavigationProps
    onAction?: () => void | Promise<void>
}

export const EmptyState = ({
    small,
    layout,
    illustrationSrc,
    title,
    subtitle,
    actionCaption,
    actionLinkProps,
    onAction,
}: EmptyStateProps) => {
    const [isWorking, setWorking] = useState(false)

    const handleAction = useCallback(async () => {
        if (onAction) {
            setWorking(true)
            await onAction()
            setWorking(false)
        }
    }, [onAction])

    const element = (
        <div className="flex flex-col items-center justify-center">
            <div
                className={classNames('border-divide justify-center0 flex items-center', {
                    'size-40 rounded-2xl': !small,
                    'size-24 rounded-lg': small,
                })}
            >
                <img src={illustrationSrc} className="w-full" />
            </div>
            <div className={classNames('font-medium', { 'mt-6 text-xl': !small, 'mt-4 text-base': small })}>
                {title}
            </div>
            <div
                className={classNames('text-secondary max-w-96 text-center', {
                    'mt-3 text-base': !small,
                    'mt-1': small,
                })}
            >
                {subtitle}
            </div>
            {actionCaption && (onAction || actionLinkProps) && (
                <div
                    className={classNames('w-full max-w-96 text-center font-medium', {
                        'border-divide mt-6 border-t pt-6 text-base': !small,
                        'mt-4': small,
                    })}
                >
                    <Link
                        className={classNames('text-link inline-flex items-center gap-x-2', {
                            'text-link-secondary pointer-events-none': isWorking,
                        })}
                        {...actionLinkProps}
                        onClick={handleAction}
                    >
                        {actionCaption}
                        {isWorking && <Spinner className="size-4" />}
                    </Link>
                </div>
            )}
        </div>
    )

    if (layout !== 'default') {
        return (
            <div
                className={classNames({
                    'absolute bottom-0 left-0 right-0 top-0 z-50 flex items-center justify-center bg-black/50':
                        layout === 'overlay',
                    'flex h-full w-full items-center justify-center': layout === 'container',
                })}
            >
                {element}
            </div>
        )
    }

    return element
}

const NoAcess = (props: Pick<EmptyStateProps, 'small' | 'layout'>) => {
    const { t } = useTranslation()

    const i18nKey = 'components.emptyState.noAccess'

    return (
        <EmptyState
            {...props}
            illustrationSrc={Illustration}
            title={t(`${i18nKey}.title`)}
            subtitle={t(`${i18nKey}.subtitle`)}
        />
    )
}

EmptyState.NoAccess = NoAcess
